/**
 * Icon Button Component
 *
 * Specialized button for icon-only buttons with consistent sizing and accessibility.
 * Automatically handles icon sizing and requires aria-label for accessibility.
 *
 * Usage:
 *   <IconButton aria-label="Close" onClick={onClose}>
 *     <X className="h-4 w-4" />
 *   </IconButton>
 *
 *   <IconButton aria-label="Delete item" variant="destructive" size="icon-sm" onClick={onDelete}>
 *     <Trash2 className="h-3.5 w-3.5" />
 *   </IconButton>
 */

import * as React from 'react';
import { Button, ButtonProps } from './button';
import { cn } from '@/lib/utils';

export interface IconButtonProps extends Omit<ButtonProps, 'size' | 'children'> {
  /**
   * Icon to display (required)
   */
  icon: React.ReactNode;
  /**
   * Size of the icon button
   * @default 'icon'
   */
  size?: 'icon' | 'icon-sm' | 'icon-lg';
  /**
   * ARIA label (required for accessibility)
   */
  'aria-label': string;
  /**
   * Variant styling
   * @default 'ghost'
   */
  variant?: ButtonProps['variant'];
}

/**
 * IconButton - Icon-only button with consistent sizing
 * Used for: Close buttons, delete buttons, action icons, toolbar buttons
 */
export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      className,
      icon,
      size = 'icon',
      variant = 'ghost',
      'aria-label': ariaLabel,
      ...props
    },
    ref,
  ) => {
    if (!ariaLabel && process.env.NODE_ENV === 'development') {
      console.warn('IconButton: aria-label is required for accessibility');
    }

    // Default to 'button' type to prevent form submission unless explicitly set
    const buttonType = props.type || 'button';
    
    return (
      <Button
        ref={ref}
        variant={variant}
        size={size}
        className={cn(className)}
        aria-label={ariaLabel}
        {...props}
        type={buttonType} // Default to 'button' to prevent form submission (explicit type in props will override)
      >
        {icon}
      </Button>
    );
  },
);
IconButton.displayName = 'IconButton';
